// Personal vocabulary — words a learner harvests by revealing a Reference Card
// in the AI trainer. Each becomes a flashcard with its own Leitner schedule, so
// "I knew it" pushes it further out and it surfaces less over time.
//
// Owner-scoped `personal_vocab` collection; all writes require auth. Everything
// here degrades quietly to a no-op for guests / when the backend is unreachable.
import { pb } from "./pocketbase.js";
import { isAuthed, currentUser } from "./auth.js";
import { nextSchedule } from "./srs.js";

// Add a revealed word to the user's personal deck. De-duplicated per (user, es)
// so flipping the same card twice — or meeting the same word again — never
// creates a duplicate. Returns { added } so the UI can confirm.
export async function addPersonalWord({ word, es, pos, sentence, tier, theme } = {}) {
  if (!isAuthed()) return { added: false, reason: "guest" };
  const uid = currentUser()?.id;
  const esVal = (es || "").trim();
  if (!uid || !esVal) return { added: false, reason: "empty" };

  try {
    // Already saved? (filter values are bound safely via pb.filter)
    try {
      await pb
        .collection("personal_vocab")
        .getFirstListItem(pb.filter("user_id = {:uid} && es = {:es}", { uid, es: esVal }));
      return { added: false, reason: "exists" };
    } catch (_) {
      /* not found → create below */
    }
    await pb.collection("personal_vocab").create({
      user_id: uid,
      word: word || "",
      es: esVal,
      pos: pos || "",
      sentence: sentence || "",
      tier: tier || "",
      theme: theme || "",
    });
    return { added: true };
  } catch (_) {
    return { added: false, reason: "error" };
  }
}

// The user's saved words, earliest-due first (new cards — no schedule yet —
// sort first, so they enter the rotation immediately).
export async function fetchPersonalVocab() {
  if (!isAuthed()) return [];
  const uid = currentUser()?.id;
  if (!uid) return [];
  try {
    return await pb.collection("personal_vocab").getFullList({
      filter: pb.filter("user_id = {:uid}", { uid }),
      sort: "srs_due_at",
    });
  } catch (_) {
    return [];
  }
}

// Roll a personal word's spaced-repetition schedule forward. rating is
// "known" | "learning"; the Leitner maths lives in lib/srs.js. Best-effort.
export async function recordPersonalReview(id, rating) {
  if (!isAuthed() || !id) return;
  try {
    const row = await pb.collection("personal_vocab").getOne(id);
    await pb.collection("personal_vocab").update(id, nextSchedule(row, rating));
  } catch (_) {
    /* best effort — a failed schedule write must never break the deck */
  }
}
